import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useQuery } from 'react-query';
import { socialAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Analytics = () => {
  const [timeframe, setTimeframe] = React.useState('7d');

  const { data: socialData, isLoading } = useQuery(
    ['socialData', timeframe],
    () => socialAPI.getAllData(timeframe),
    { refetchInterval: 60000 }
  );

  const handleTimeframeChange = (event) => {
    setTimeframe(event.target.value);
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  const platforms = socialData?.platforms || {};
  const platformNames = Object.keys(platforms);

  // Prepare data for charts
  const followerData = platformNames.map(platform => ({
    platform: platform.charAt(0).toUpperCase() + platform.slice(1),
    followers: platforms[platform].followers || 0
  }));

  const engagementData = platformNames.map(platform => ({
    platform: platform.charAt(0).toUpperCase() + platform.slice(1),
    engagement: platforms[platform].total_engagement || 0
  }));

  const engagementRateData = platformNames.map(platform => ({
    platform: platform.charAt(0).toUpperCase() + platform.slice(1),
    rate: platforms[platform].engagement_rate || 0
  }));

  const COLORS = ['#E4405F', '#FF0000', '#1877F2', '#000000', '#FFFC00'];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Analytics Dashboard
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body1" color="text.secondary">
            Detailed insights into your social media performance
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={timeframe}
              label="Timeframe"
              onChange={handleTimeframeChange}
            >
              <MenuItem value="1d">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {platformNames.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect your social media accounts to see analytics
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {/* Followers Chart */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Followers by Platform
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={followerData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                    <Bar dataKey="followers" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Engagement Chart */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Engagement by Platform
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={engagementData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                    <Bar dataKey="engagement" fill="#dc004e" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Engagement Rate Chart */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Engagement Rate by Platform
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={engagementRateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                    <Line 
                      type="monotone" 
                      dataKey="rate" 
                      stroke="#2e7d32" 
                      strokeWidth={2}
                      dot={{ fill: '#2e7d32', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Platform Distribution */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Platform Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={followerData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ platform, percent }) => `${platform} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="followers"
                    >
                      {followerData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Detailed Platform Stats */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detailed Platform Statistics
                </Typography>
                <Grid container spacing={2}>
                  {platformNames.map((platform) => {
                    const data = platforms[platform];
                    return (
                      <Grid item xs={12} sm={6} md={4} key={platform}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1 }}>
                            {platform}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Followers:
                            </Typography>
                            <Typography variant="body2">
                              {data.followers?.toLocaleString() || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Posts:
                            </Typography>
                            <Typography variant="body2">
                              {data.posts_count || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Engagement:
                            </Typography>
                            <Typography variant="body2">
                              {data.total_engagement?.toLocaleString() || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Engagement Rate:
                            </Typography>
                            <Typography variant="body2">
                              {data.engagement_rate?.toFixed(2) || 0}%
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Analytics;
